//
/* GS://PROTOCOLO
 * Arquivo: src/pages/assembly/AssemblyOrdersPage.js
 * Dossiê de Projeto: Ecossistema GoldenSisters
 * Versão: 2.0 – Lista Inteligente com Ação de Conclusão (Baixa + Criação de Produto)
 * Impacto da Versão: [src/pages/assembly/AssemblyOrdersPage.js]
 * Data: 20/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Botão de ação "Concluir" para ordens ABERTA.
 * - LÓGICA: handleFinishAssembly executa a baixa de insumos (via serviço).
 * - LÓGICA: Cria ou atualiza o produto final (Kit) no estoque ao concluir.
 * - LÓGICA: Atualiza status da ordem para CONCLUIDA.
 */
//

import React, { useEffect, useState } from 'react';
import { 
  collection, 
  getDocs, 
  orderBy, 
  query, 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc, 
  where, 
  serverTimestamp, 
  increment 
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { GSM_COLLECTIONS } from '../../services/gs_nf_module_dataModel';
import stockMovementsService from '../../services/stockMovementsService';
import {
  ClipboardList,
  PlusCircle,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Play,
  ChevronRight,
  PackageCheck,
  Loader2
} from 'lucide-react';

const STATUS_LABELS = {
  ABERTA: 'Aberta (Reservado)',
  EM_EXECUCAO: 'Em execução',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const STATUS_COLORS = {
  ABERTA: 'bg-yellow-50 text-yellow-800 border border-yellow-300',
  EM_EXECUCAO: 'bg-blue-50 text-blue-800 border border-blue-300',
  CONCLUIDA: 'bg-emerald-50 text-emerald-800 border border-emerald-300',
  CANCELADA: 'bg-red-50 text-red-800 border border-red-300',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    if (value.toDate) return value.toDate().toLocaleString('pt-BR');
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('pt-BR');
  } catch { return '-'; }
};

const renderStatusBadge = (statusRaw) => {
  const status = statusRaw || 'ABERTA';
  const label = STATUS_LABELS[status] || status;
  const badgeClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border border-gray-300';

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
      {status === 'CONCLUIDA' && <CheckCircle2 className="w-3 h-3 mr-1.5" />}
      {status === 'CANCELADA' && <XCircle className="w-3 h-3 mr-1.5" />}
      {status === 'EM_EXECUCAO' && <Play className="w-3 h-3 mr-1.5" />}
      {status === 'ABERTA' && <AlertCircle className="w-3 h-3 mr-1.5" />}
      {label}
    </span>
  );
};

export default function AssemblyOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [processingId, setProcessingId] = useState(null); // ID da ordem sendo processada
  const [error, setError] = useState('');

  const loadOrders = async () => {
    setError('');
    setReloading(true);
    try {
      const q = query(
        collection(db, GSM_COLLECTIONS.ASSEMBLIES),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setOrders(list);
    } catch (err) {
      console.error('Erro ao carregar Ordens:', err);
      setError('Não foi possível carregar as Ordens de Montagem.');
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { loadOrders(); }, []);

  // --------------------------------------------------------
  // LÓGICA DE FINALIZAÇÃO (BAIXA DE ESTOQUE + CRIAÇÃO DE PRODUTO)
  // --------------------------------------------------------
  const handleFinishAssembly = async (orderId) => {
    if (!window.confirm("Confirma a conclusão da montagem?\n\nIsso irá:\n1. Baixar definitivamente os insumos do estoque.\n2. Adicionar os Kits produzidos ao estoque de venda.")) return;

    setProcessingId(orderId);
    try {
      // 1. Busca dados completos da ordem
      const orderRef = doc(db, GSM_COLLECTIONS.ASSEMBLIES, orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) throw new Error("Ordem não encontrada.");
      
      const order = orderSnap.data();
      const kitQty = Number(order.orderQuantity) || 0;
      const components = order.components || [];

      if (kitQty <= 0) throw new Error("Quantidade de Kits inválida.");

      // 2. Baixa de Insumos (Confirma Reserva)
      for (const comp of components) {
        const totalQtyToConsume = comp.qtyUsed * kitQty;
        await stockMovementsService.releaseOrConfirmReserve({
          productCollection: 'products',
          productId: comp.id,
          quantity: totalQtyToConsume,
          mode: 'confirm', // Baixa real
          assemblyId: orderId,
          notes: `Baixa por conclusão de montagem: ${order.orderReference}`
        });
      }

      // 3. Criação ou Atualização do Produto Final (Kit)
      if (order.finalSku) {
        const productsRef = collection(db, 'products');
        const qProduct = query(productsRef, where('sku', '==', order.finalSku));
        const productSnap = await getDocs(qProduct);

        if (!productSnap.empty) {
          // Produto já existe: Soma estoque
          const existingDoc = productSnap.docs[0];
          await updateDoc(doc(db, 'products', existingDoc.id), {
            stock: increment(kitQty),
            updatedAt: serverTimestamp()
          });
        } else {
          // Produto novo: Cria cadastro
          await addDoc(productsRef, {
            name: order.orderDescription || `Kit Montado ${order.orderReference}`,
            sku: order.finalSku,
            price: Number(order.decidedPrice) || 0,
            cost: Number(order.totalCost) || 0,
            stock: kitQty,
            productType: 'venda',
            status: 'Disponível',
            location: 'Em Loja',
            description: `Produto gerado automaticamente pela Ordem de Montagem ${order.orderReference}`,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }

      // 4. Atualiza status da Ordem
      await updateDoc(orderRef, {
        status: 'CONCLUIDA',
        finishedAt: serverTimestamp()
      });

      alert("Montagem concluída com sucesso! Estoque atualizado.");
      loadOrders();

    } catch (err) {
      console.error(err);
      alert(`Erro ao finalizar: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-brand-black px-6 py-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="flex items-center text-2xl font-semibold text-brand-gold">
            <ClipboardList className="w-7 h-7 mr-3" /> Ordens de Montagem
          </h1>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Gerencie a produção interna. Finalize as ordens para transformar insumos reservados em produtos de venda.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadOrders}
            disabled={reloading}
            className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${reloading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
          <button
            onClick={() => navigate('/assembly/orders/new')}
            className="inline-flex items-center px-4 py-2 text-sm rounded-lg bg-brand-gold text-brand-black font-semibold shadow-md hover:brightness-110 transition-colors"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Nova Ordem
          </button>
        </div>
      </div>

      {!loading && error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {orders.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-600">
              Nenhuma Ordem de Montagem registrada.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Referência / SKU</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Qtd. Kits</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Criada em</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">{order.orderReference || '—'}</span>
                        {order.finalSku && <span className="text-xs text-gray-500 font-mono">{order.finalSku}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-900">{order.orderQuantity || 1}</td>
                    <td className="px-4 py-3 align-top text-gray-900">{order.assemblyType === 'COMPLEXA' ? 'Complexa' : 'Simples'}</td>
                    <td className="px-4 py-3 align-top text-gray-900">{formatDateTime(order.createdAt)}</td>
                    <td className="px-4 py-3 align-top">{renderStatusBadge(order.status)}</td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-2">
                        {order.status === 'ABERTA' && (
                          <button
                            onClick={() => handleFinishAssembly(order.id)}
                            disabled={processingId === order.id}
                            title="Concluir Montagem e Baixar Estoque"
                            className="inline-flex items-center px-3 py-1.5 text-xs rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200 transition-colors font-bold"
                          >
                            {processingId === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}
                            Concluir
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/assembly/orders/${order.id}`)}
                          className="inline-flex items-center px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          Detalhe <ChevronRight className="w-3 h-3 ml-1" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}